'use client';

import { useState } from 'react';
import Link from 'next/link';

import { useAnnouncements } from './announcements-context';
import { type AnnouncementPayload } from './request-announcements';

type AnnouncementsBannerProps = {
  className?: string;
};

type AlertIconVariant = 'high' | 'medium' | 'low';

function AlertIcon({ variant = 'high' }: { variant?: AlertIconVariant }) {
  if (variant === 'low') {
    return (
      <span
        className="inline-flex h-8 min-h-8 w-8 min-w-8 shrink-0 animate-pulse items-center justify-center rounded-full bg-white text-lg font-bold text-blue-600"
        aria-hidden
      >
        i
      </span>
    );
  }

  if (variant === 'medium') {
    return (
      <span
        className="inline-flex h-8 min-h-8 w-8 min-w-8 shrink-0 animate-pulse items-center justify-center rounded-full bg-white text-lg font-bold text-amber-600"
        aria-hidden
      >
        !
      </span>
    );
  }

  return (
    <span
      className="inline-flex h-8 min-h-8 w-8 min-w-8 shrink-0 animate-pulse items-center justify-center rounded-full bg-white text-lg font-bold text-red-600"
      aria-hidden
    >
      ⚠
    </span>
  );
}

function ChevronIcon({
  expanded,
  className = '',
}: {
  expanded: boolean;
  className?: string;
}) {
  return (
    <svg
      className={`h-5 w-5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''} ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function AnnouncementRow({
  announcement,
}: {
  announcement: AnnouncementPayload;
}) {
  const { readIdSet, markRead } = useAnnouncements();
  const isRead = !announcement.uuid || readIdSet.has(announcement.uuid);
  const hasLink = Boolean(announcement.cta_url);

  return (
    <div className="border-b border-slate-200 px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-slate-900">
            {announcement.name}
          </h4>
          <p className="mt-1 text-base text-slate-700">
            {announcement.details}
          </p>
          {hasLink && (
            <Link
              target="_blank"
              href={announcement.cta_url ?? '#'}
              className="mt-2 inline-flex text-sm font-semibold text-blue-700 underline-offset-2 hover:underline"
            >
              {announcement.cta_text || 'More Info'}
            </Link>
          )}
        </div>
        {!isRead && announcement.type?.toLowerCase() !== 'urgent' && (
          <button
            type="button"
            onClick={() => {
              if (announcement.uuid) void markRead([announcement.uuid]);
            }}
            className="shrink-0 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            Mark read
          </button>
        )}
      </div>
    </div>
  );
}

export function AnnouncementsBanner({
  className = '',
}: AnnouncementsBannerProps) {
  const {
    highPriorityAnnouncements,
    mediumPriorityAnnouncements,
    lowPriorityAnnouncements,
    showHighPriorityToolbar,
    showMediumPriorityToolbar,
    showLowPriorityToolbar,
  } = useAnnouncements();

  const [highOpen, setHighOpen] = useState(false);
  const [mediumOpen, setMediumOpen] = useState(false);
  const [lowOpen, setLowOpen] = useState(false);

  if (
    !showHighPriorityToolbar &&
    !showMediumPriorityToolbar &&
    !showLowPriorityToolbar
  ) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {showHighPriorityToolbar && (
        <section className="relative">
          <button
            type="button"
            onClick={() => {
              setMediumOpen(false);
              setLowOpen(false);
              setHighOpen((prev) => !prev);
            }}
            className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-4 py-3 text-left text-white shadow-md"
          >
            <div className="flex min-w-0 items-center gap-4">
              <AlertIcon variant="high" />
              <div className="min-w-0">
                <p className="truncate text-lg font-bold">
                  {highPriorityAnnouncements
                    .map((item) => item.name)
                    .join(', ')}
                </p>
              </div>
            </div>
            <ChevronIcon expanded={highOpen} className="h-6 w-6 shrink-0" />
          </button>
          {highOpen && (
            <div className="absolute left-0 right-0 top-full z-50 max-h-[50vh] overflow-y-auto border-b-2 border-red-300 bg-white shadow-xl">
              {highPriorityAnnouncements.map((announcement) => (
                <AnnouncementRow
                  key={announcement.uuid}
                  announcement={announcement}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {showMediumPriorityToolbar && (
        <section className="relative">
          <button
            type="button"
            onClick={() => {
              setHighOpen(false);
              setLowOpen(false);
              setMediumOpen((prev) => !prev);
            }}
            className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 px-4 py-3 text-left text-white shadow-md"
          >
            <div className="flex min-w-0 items-center gap-4">
              <AlertIcon variant="medium" />
              <div className="min-w-0">
                <p className="truncate text-lg font-bold">
                  {mediumPriorityAnnouncements
                    .map((item) => item.name)
                    .join(', ')}
                </p>
              </div>
            </div>
            <ChevronIcon expanded={mediumOpen} className="h-6 w-6 shrink-0" />
          </button>
          {mediumOpen && (
            <div className="absolute left-0 right-0 top-full z-50 max-h-[50vh] overflow-y-auto border-b-2 border-amber-300 bg-white shadow-xl">
              {mediumPriorityAnnouncements.map((announcement) => (
                <AnnouncementRow
                  key={announcement.uuid}
                  announcement={announcement}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {showLowPriorityToolbar && (
        <section className="relative">
          <button
            type="button"
            onClick={() => {
              setHighOpen(false);
              setMediumOpen(false);
              setLowOpen((prev) => !prev);
            }}
            className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 px-4 py-3 text-left text-white shadow-md"
          >
            <div className="flex min-w-0 items-center gap-4">
              <AlertIcon variant="low" />
              <div className="min-w-0">
                <p className="truncate text-lg font-bold">
                  {lowPriorityAnnouncements.map((item) => item.name).join(', ')}
                </p>
              </div>
            </div>
            <ChevronIcon expanded={lowOpen} className="h-6 w-6 shrink-0" />
          </button>
          {lowOpen && (
            <div className="absolute left-0 right-0 top-full z-50 max-h-[50vh] overflow-y-auto border-b-2 border-blue-300 bg-white shadow-xl">
              {lowPriorityAnnouncements.map((announcement) => (
                <AnnouncementRow
                  key={announcement.uuid}
                  announcement={announcement}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
